import { useEffect } from 'react'

export const useGroupWatchVisibility = ({
  hasSignedIn,
  groupWatch,
  groupWatchId,
  mayRefetch,
  onGroupIsNotLive,
  onUserSignedOut,
  refetchGroup,
}) => {
  const { userStatus, groupStatus } = groupWatch || {}

  // Attempt refetch of group details if needed
  useEffect(() => {
    // There are cases where the group details from the API might be out of date:
    //
    // 1. If host starts streaming immediately after creating group
    // 2. If a guest joins a group watch and then goes to the video immediately, they won't be a guest
    // 3. If host starts, then guest joins immediately, the group status will appear as `created` not `started`

    const interval = setInterval(() => {
      const videoPlayDisabled =
        !userStatus || !groupStatus || userStatus === 'non_guest' || groupStatus === 'created'

      if (mayRefetch && videoPlayDisabled) {
        refetchGroup()
      } else {
        clearInterval(interval)
      }
    }, 2000)

    return () => clearInterval(interval)
  }, [mayRefetch, groupStatus, refetchGroup, userStatus])

  const userIsValid = getUserIsValid(groupStatus, userStatus)

  // If the group is not valid/live after the max refetches the app should take
  // appropriate action such as redirecting away with a message.
  // This is common to all apps, and treated the same as a valid group session reaching end of life
  useEffect(() => {
    if ((!userIsValid && !mayRefetch) || (groupStatus && !getGroupStatusIsValid(groupStatus))) {
      onGroupIsNotLive()
    }
  }, [userIsValid, mayRefetch, onGroupIsNotLive, groupStatus])

  // Redirect signed out users to the guest screen
  useEffect(() => {
    if (!hasSignedIn && groupWatchId) {
      onUserSignedOut()
    }
  }, [hasSignedIn, groupWatchId, onUserSignedOut])

  return {
    groupPossiblyInvalid:
      !groupWatch || !getGroupStatusIsValid(groupWatch?.groupStatus) || !userIsValid, // a loading state, since handled via cb after max retries
    groupHasStarted: groupWatch?.groupStatus === 'started',
    userIsHost: userStatus === 'host',
  }
}

const getGroupStatusIsValid = (groupStatus) => ['started', 'created'].includes(groupStatus)

const getUserIsValid = (groupStatus, userStatus) => {
  switch (userStatus) {
    case 'guest':
      return groupStatus === 'started'
    case 'host':
      return ['created', 'started'].includes(groupStatus)
    default:
      return false
  }
}
